import React, { useState, useEffect } from 'react';
import { Clock, Bell, AlertCircle, CheckCircle, Info, XCircle, Mail, MessageSquare, ChevronDown, ChevronUp } from 'lucide-react';
import { activityService, DashboardNotification } from '@/services/activityService';
import './RecentActivity.css';

export function RecentActivity() {
  const [notifications, setNotifications] = useState<DashboardNotification[]>([]);
  const [originalNotifications, setOriginalNotifications] = useState<DashboardNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedLoading, setExpandedLoading] = useState(false);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const data = await activityService.getDashboardNotifications(0, 10);
        setNotifications(data);
        setOriginalNotifications(data); // Store original 10 notifications
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, []);

  const handleViewAll = async () => {
    if (isExpanded) {
      // Collapse: restore original 10 notifications
      setIsExpanded(false);
      setNotifications(originalNotifications);
    } else {
      // Expand: fetch all 100 notifications
      setExpandedLoading(true);
      try {
        const data = await activityService.getDashboardNotifications(0, 100);
        setNotifications(data);
        setIsExpanded(true);
      } catch (error) {
        console.error('Failed to fetch all notifications:', error);
      } finally {
        setExpandedLoading(false);
      }
    }
  };

  const getIcon = (type: string) => {
    const iconClass = "w-4 h-4";
    
    switch (type.toLowerCase()) {
      case 'info':
        return <Info className={`${iconClass} text-blue-600`} />;
      case 'success':
        return <CheckCircle className={`${iconClass} text-emerald-600`} />;
      case 'warning':
        return <AlertCircle className={`${iconClass} text-orange-600`} />;
      case 'error':
        return <XCircle className={`${iconClass} text-red-600`} />;
      case 'message':
        return <MessageSquare className={`${iconClass} text-purple-600`} />;
      case 'email':
        return <Mail className={`${iconClass} text-indigo-600`} />;
      default:
        return <Bell className={`${iconClass} text-gray-600`} />;
    }
  };

  const getStatusIcon = (status: string) => {
    const iconClass = "w-2 h-2";
    
    switch (status.toLowerCase()) {
      case 'read':
        return <CheckCircle className={`${iconClass} text-green-500`} />;
      case 'unread':
        return <Bell className={`${iconClass} text-blue-500`} />;
      case 'sent':
        return <CheckCircle className={`${iconClass} text-emerald-500`} />;
      case 'pending':
        return <Clock className={`${iconClass} text-orange-500`} />;
      default:
        return <Bell className={`${iconClass} text-gray-500`} />;
    }
  };

  const formatTime = (timestamp: string) => {
    const activityTime = new Date(timestamp);
    
    // Day names
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const dayName = dayNames[activityTime.getDay()];
    
    // Month names
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
    const monthName = monthNames[activityTime.getMonth()];
    
    // Date components
    const day = activityTime.getDate();
    const year = activityTime.getFullYear();
    
    // Time in 12-hour format with AM/PM
    let hours = activityTime.getHours();
    const minutes = activityTime.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 should be 12
    const minutesStr = String(minutes).padStart(2, '0');
    
    return `${dayName}, ${day} ${monthName} ${year} at ${hours}:${minutesStr} ${ampm}`;
  };

  return (
    <div className="relative bg-white rounded-3xl shadow-[0_20px_60px_-12px_rgba(0,0,0,0.25)] border border-gray-200/50 overflow-hidden ring-1 ring-gray-900/5">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50/40 via-indigo-50/30 to-purple-50/40"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.08),transparent_50%)]"></div>
      <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(255,255,255,0.1)_0%,rgba(255,255,255,0.05)_50%,transparent_100%)]"></div>
      
      {/* Header */}
      <div className="relative p-8 border-b border-gray-200/60 backdrop-blur-sm" style={{ backgroundColor: 'rgba(0, 31, 84, 0.8)' }}>
        <div className="absolute inset-0 bg-gradient-to-r from-white/10 via-blue-50/20 to-indigo-50/10"></div>
        <div className="relative flex items-center justify-between">
          <div>
            <h3 className="text-2xl font-bold text-white drop-shadow-sm">
              Notifications
            </h3>
            <p className="text-sm text-white/80 mt-1 font-medium">Recent notifications and updates</p>
          </div>
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-xl ring-2 ring-blue-400/30 ring-offset-2 ring-offset-white/50">
            <Bell className="w-6 h-6 text-white drop-shadow-lg" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative p-8">
        {loading ? (
          <div className="space-y-4 max-h-[500px] overflow-y-auto">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-start space-x-4 animate-pulse">
                <div className="w-12 h-12 bg-gray-200 rounded-2xl"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Bell className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-gray-500 text-lg font-medium">No notifications</p>
            <p className="text-gray-400 text-sm mt-1">Notifications will appear here when available</p>
          </div>
        ) : (
          <div 
            className={`space-y-3 overflow-y-auto pr-2 custom-scrollbar transition-all duration-300 ${
              isExpanded ? 'max-h-[800px]' : 'max-h-[500px]'
            }`}
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: '#cbd5e1 #f1f5f9'
            }}
          >
            {notifications.map((notification, index) => (
              <div 
                key={notification.id} 
                className="group relative flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50/80 transition-all duration-200 hover:shadow-sm border border-transparent hover:border-gray-200/60"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Notification Icon */}
                <div className="relative flex-shrink-0">
                  <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center shadow-sm border border-gray-200/50 group-hover:bg-blue-50 group-hover:border-blue-200/50 transition-all duration-200">
                    {getIcon(notification.type)}
                  </div>
                  {/* Status Indicator */}
                  <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-white rounded-full flex items-center justify-center shadow-sm border border-gray-200/50">
                    {getStatusIcon(notification.status)}
                  </div>
                </div>

                {/* Notification Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-gray-900 group-hover:text-blue-900 transition-colors duration-200">
                        {notification.title}
                      </h4>
                      <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">
                        {notification.message}
                      </p>
                      {notification.related_activity && (
                        <div className="flex items-center mt-1 space-x-1">
                          <div className="w-3 h-3 bg-purple-100 rounded-full flex items-center justify-center">
                            <Info className="w-2 h-2 text-purple-600" />
                          </div>
                          <span className="text-xs text-gray-500">
                            Related: {notification.related_activity.description}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex-shrink-0 ml-3">
                      <span className="text-xs text-gray-500 bg-gray-50 px-2 py-0.5 rounded">
                        {formatTime(notification.created_at)}
                      </span>
                      {notification.read_at && (
                        <div className="text-xs text-green-600 mt-0.5 flex items-center">
                          <CheckCircle className="w-2 h-2 mr-0.5" />
                          Read
                        </div>
                      )}
                    </div>
                  </div>
                </div>

              </div>
            ))}
          </div>
        )}

        {/* View All Button */}
        {!loading && notifications.length > 0 && (
          <div className="mt-8 text-center">
            <button 
              onClick={handleViewAll}
              disabled={expandedLoading}
              className="inline-flex items-center px-6 py-3 text-sm font-semibold text-blue-700 bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 rounded-2xl transition-all duration-300 hover:shadow-lg hover:scale-105 border border-blue-200/50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {expandedLoading ? (
                <>
                  <div className="w-4 h-4 mr-2 border-2 border-blue-700 border-t-transparent rounded-full animate-spin"></div>
                  Loading...
                </>
              ) : (
                <>
                  {isExpanded ? (
                    <>
                      <ChevronUp className="w-4 h-4 mr-2" />
                      Show Less
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4 mr-2" />
                      View All Notifications
                    </>
                  )}
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
